import { useAppSelector } from "@/redux/hooks";
import { fetchProductMetadataWithGraphQL } from "@/redux/reducers/checkout/thunk";
import { useApolloClient } from "@apollo/client";
import { TrackingPixel } from "@brandclub/common-ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCheckoutUI } from "../CheckoutUIProvider";
import { CheckoutIntent } from "../types/CheckoutIntent";

export const useCheckoutCartInfo = () => {
  const [state] = useCheckoutUI();
  // @ts-ignore
  const context = state.context as any;
  const {
    checkoutIntent,
    checkoutIntentLoading,
    itemsToCheckout,
    checkoutSummaryLoading,
  } = context as {
    checkoutIntent: CheckoutIntent;
    checkoutIntentLoading: boolean;
    itemsToCheckout: {
      retailerId: number;
      retailerSku: string;
      stacklineSku: string;
      quantity: number;
    }[];
    checkoutSummaryLoading?: boolean;
  };

  const isAuthenticated = useAppSelector(
    (state) => !!state.userProfile?.userId
  );

  const retailers = useAppSelector((state) => state.retailers);

  const apolloClient = useApolloClient();

  const metadataCacheRef = useRef<Record<string, any>>({});

  const fetchProductMetadataWithCache = useCallback(
    async ({
      stacklineSkus,
      retailerIds,
      retailerSkus,
    }: {
      stacklineSkus: string[];
      retailerIds?: number[];
      retailerSkus?: string[];
    }): Promise<Record<string, any>> => {
      if (!stacklineSkus.length) return [];
      const stacklineSkusToFetch = stacklineSkus.filter(
        (sku) => !metadataCacheRef.current[sku] && !!sku
      );

      if (
        stacklineSkusToFetch.length === 0 &&
        retailerIds?.length === 0 &&
        retailerSkus?.length === 0
      ) {
        return stacklineSkus.map((sku) => metadataCacheRef.current[sku]);
      }

      const newMetadata = await fetchProductMetadataWithGraphQL({
        apolloClient,
        stacklineSkus: stacklineSkusToFetch,
        isAuthenticated,
        retailerIds,
        retailerSkus,
      });

      newMetadata.forEach((item) => {
        if (item.stacklineSku) {
          metadataCacheRef.current[item.stacklineSku] = {
            retailerId: item.retailerId,
            retailerSku: item.retailerSku,
            retailerParentSku: item.retailerParentSku,
            stacklineSku: item.stacklineSku,
            retailPrice: item.retailPrice,
            brandId: item.brandId,
            brandName: item.brandName,
            categoryId: item.categoryId,
            categoryName: item.categoryName,
            subCategoryId: item.subCategoryId,
            subCategoryName: item.subCategoryName,
            totalRewardAmount: item.totalRewardAmount,
            pixels: item.pixels,
          };
        }
      });

      // Return all products from newMetadata, using stacklineSku as key if available
      const result: Record<string, any> = {};

      // Add products from newMetadata
      newMetadata.forEach((item) => {
        const key =
          item.stacklineSku ||
          item.retailerSku ||
          `retailer_${item.retailerId}_${item.retailerSku}`;
        result[key] = {
          retailerId: item.retailerId,
          retailerSku: item.retailerSku,
          retailerParentSku: item.retailerParentSku,
          stacklineSku: item.stacklineSku,
          retailPrice: item.retailPrice,
          brandId: item.brandId,
          brandName: item.brandName,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          subCategoryId: item.subCategoryId,
          subCategoryName: item.subCategoryName,
          totalRewardAmount: item.totalRewardAmount,
          pixels: item.pixels,
        };
      });

      // Also add any cached items that weren't in newMetadata but were requested
      stacklineSkus
        .filter((sku) => sku && !result[sku]) // Only add if not already in result
        .forEach((sku) => {
          result[sku] = metadataCacheRef.current[sku] || null;
        });

      return result;
    },
    [apolloClient, isAuthenticated]
  );

  const [currentCart, setCurrentCart] = useState<{
    subtotal: number;
    estimatedShipping: number; // net shipping after discounts
    shippingFees?: number; // current shipping fees field from summary (may include original before discount depending on API)
    originalShippingFees?: number; // best-effort original shipping before discounts and perks
    shippingDiscount?: number; // absolute value
    estimatedTax: number;
    originalEstimatedTax?: number;
    amountToCharge: number;
    serviceFee: number; // current service fee (may be 0)
    originalServiceFee?: number; // original service fee to compare against
    items: {
      retailerId: number;
      retailerSku: string;
      retailerParentSku: string;
      stacklineSku: string;
      title: string;
      brandId: number;
      brandName: string;
      quantity: number;
      retailPrice: number;
      pixels: TrackingPixel[];
      categoryId: number;
      categoryName: string;
      subCategoryId: number;
      subCategoryName: string;
    }[];
    deliveryEstimate: string;
    source: "checkoutIntent" | "cart";
    originalTotal?: number; // original total before discounts or as guest
    rewardAmounts: {
      retailerConnectRewardAmount: number;
      purchaseRewardAmount: number;
      totalRewardAmount: number;
    };
  }>({
    serviceFee: 0,
    subtotal: 0,
    estimatedShipping: 0,
    estimatedTax: 0,
    originalEstimatedTax: undefined,
    amountToCharge: 0,
    items: [],
    deliveryEstimate: "",
    source: "checkoutIntent",
    rewardAmounts: {
      retailerConnectRewardAmount: 0,
      purchaseRewardAmount: 0,
      totalRewardAmount: 0,
    },
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCheckoutItems = async () => {
      setIsLoading(true);
      if (checkoutIntent && isAuthenticated && !checkoutIntentLoading) {
        const retailer = retailers.find(
          (retailer) => retailer.retailerId === checkoutIntent.retailerId
        );
        const productsMetadataBySku = await fetchProductMetadataWithCache({
          stacklineSkus:
            checkoutIntent.items?.map((item) => item.stacklineSku) || [],
          retailerIds: [checkoutIntent.retailerId],
          retailerSkus:
            checkoutIntent.items?.map((item) => item.retailerSku) || [],
        });
        const purchaseRewardAmount = Object.values(
          productsMetadataBySku
        ).reduce((acc, item) => acc + (item?.totalRewardAmount ?? 0), 0);

        // Shipping calculations (fees, discount, net)
        const shippingFees =
          checkoutIntent.estimatedOrderSummary?.shippingFees || 0;
        const shippingDiscount = Math.abs(
          checkoutIntent.estimatedOrderSummary?.shippingDiscount || 0
        );
        const netShipping = Math.max(shippingFees - shippingDiscount, 0);

        // Determine an original shipping to strike through: prefer the maximum among guest/user fees and the pre-discount fee
        const shippingCandidates = [
          checkoutIntent.estimatedGuestOrderSummary?.shippingFees,
          checkoutIntent.estimatedUserAccountOrderSummary?.shippingFees,
          shippingFees,
          shippingDiscount > 0 ? shippingFees : undefined,
        ].filter((v) => typeof v === "number") as number[];
        const originalShippingFees = shippingCandidates.length
          ? Math.max(...shippingCandidates)
          : undefined;

        // Service fee comparison (show original if waived)
        const currentServiceFee =
          checkoutIntent.estimatedOrderSummary?.serviceFee || 0;
        const guestServiceFee =
          checkoutIntent.estimatedGuestOrderSummary?.serviceFee ?? undefined;
        const userServiceFee =
          checkoutIntent.estimatedUserAccountOrderSummary?.serviceFee ??
          undefined;
        const possibleOriginalServiceFees = [
          guestServiceFee,
          userServiceFee,
          shippingFees > 0 ? undefined : undefined,
        ].filter((v) => typeof v === "number") as number[];
        const maxOriginalServiceFee = possibleOriginalServiceFees.length
          ? Math.max(...possibleOriginalServiceFees)
          : undefined;
        const originalServiceFee =
          maxOriginalServiceFee && maxOriginalServiceFee > currentServiceFee
            ? maxOriginalServiceFee
            : undefined;

        const subtotal = checkoutIntent.estimatedOrderSummary?.subtotal || 0;
        const estimatedTax = checkoutIntent.estimatedOrderSummary?.tax || 0;
        const amountToCharge = checkoutIntent.estimatedOrderSummary?.total || 0;

        // Original total comparison: compare guest/user totals and also add back discounts
        const compareTotals = [
          checkoutIntent.estimatedGuestOrderSummary?.total,
          checkoutIntent.estimatedUserAccountOrderSummary?.total,
          amountToCharge + shippingDiscount,
        ].filter((n) => typeof n === "number") as number[];
        const computedOriginalTotal = compareTotals.length
          ? Math.max(...compareTotals)
          : undefined;
        const originalTotal =
          computedOriginalTotal &&
          computedOriginalTotal > amountToCharge + 0.001
            ? computedOriginalTotal
            : undefined;
        // Original tax comparison (guest/user vs current)
        const taxComparisons = [
          checkoutIntent.estimatedGuestOrderSummary?.tax,
          checkoutIntent.estimatedUserAccountOrderSummary?.tax,
        ].filter((n) => typeof n === "number") as number[];
        const maxOriginalTax = taxComparisons.length
          ? Math.max(...taxComparisons)
          : undefined;
        const originalEstimatedTax =
          maxOriginalTax && maxOriginalTax > estimatedTax + 0.001
            ? maxOriginalTax
            : undefined;
        setCurrentCart({
          serviceFee: currentServiceFee,
          originalServiceFee,
          subtotal,
          estimatedShipping: netShipping,
          shippingFees,
          originalShippingFees,
          shippingDiscount,
          estimatedTax,
          originalEstimatedTax,
          amountToCharge,
          originalTotal,
          items:
            checkoutIntent.items?.map((item) => ({
              ...productsMetadataBySku[item.stacklineSku],
              ...item,
            })) || [],
          deliveryEstimate:
            checkoutIntent.shippingOptions?.[0]?.deliveryEstimate || "",
          rewardAmounts: {
            retailerConnectRewardAmount:
              retailer?.retailerConnectRewardAmount || 0,
            purchaseRewardAmount,
            totalRewardAmount:
              retailer?.retailerConnectRewardAmount + purchaseRewardAmount,
          },
          source: "checkoutIntent",
        });
      } else if (itemsToCheckout.length > 0) {
        const productsMetadataBySku = await fetchProductMetadataWithCache({
          stacklineSkus: itemsToCheckout.map((item) => item.stacklineSku),
          retailerIds: [itemsToCheckout[0].retailerId],
          retailerSkus: itemsToCheckout.map((item) => item.retailerSku),
        });
        const retailer = retailers.find(
          (retailer) => retailer.retailerId === itemsToCheckout[0].retailerId
        );

        const getMetadataForProduct = (item: {
          stacklineSku: string;
          retailerSku: string;
        }) => {
          if (!item?.stacklineSku && !item?.retailerSku) {
            return null;
          }
          return (
            productsMetadataBySku[item.stacklineSku] ||
            Object.values(productsMetadataBySku).find(
              (metadata) => metadata?.retailerSku === item?.retailerSku
            )
          );
        };

        // Note: not all items will have stackline sku set, so we need to use the retailer sku if it is not
        const items = itemsToCheckout.map((item) => ({
          ...getMetadataForProduct(item),
          ...item,
          retailPrice:
            productsMetadataBySku[item.stacklineSku]?.retailPrice || 0,
        }));

        setCurrentCart({
          items,
          serviceFee: 0,
          subtotal: items.reduce(
            (acc, item) => acc + item.retailPrice * item.quantity,
            0
          ),
          estimatedShipping: 0,
          shippingFees: 0,
          shippingDiscount: 0,
          estimatedTax: 0,
          originalEstimatedTax: undefined,
          amountToCharge: items.reduce(
            (acc, item) => acc + item.retailPrice * item.quantity,
            0
          ),
          originalTotal: undefined,
          deliveryEstimate: "",
          rewardAmounts: {
            retailerConnectRewardAmount:
              retailer?.retailerConnectRewardAmount || 0,
            purchaseRewardAmount: Object.values(productsMetadataBySku).reduce(
              (acc, item) => acc + (item?.totalRewardAmount ?? 0),
              0
            ),
            totalRewardAmount:
              retailer?.retailerConnectRewardAmount +
              Object.values(productsMetadataBySku).reduce(
                (acc, item) => acc + (item?.totalRewardAmount ?? 0),
                0
              ),
          },
          source: "checkoutIntent",
        });
      } else {
        setCurrentCart({
          items: [],
          serviceFee: 0,
          subtotal: 0,
          estimatedShipping: 0,
          shippingFees: 0,
          shippingDiscount: 0,
          estimatedTax: 0,
          originalEstimatedTax: undefined,
          amountToCharge: 0,
          originalTotal: undefined,
          deliveryEstimate: "",
          source: "checkoutIntent",
          rewardAmounts: {
            retailerConnectRewardAmount: 0,
            purchaseRewardAmount: 0,
            totalRewardAmount: 0,
          },
        });
      }
      setIsLoading(false);
    };

    fetchCheckoutItems();
  }, [
    checkoutIntent,
    itemsToCheckout,
    apolloClient,
    fetchProductMetadataWithCache,
    checkoutIntentLoading,
    retailers,
    isAuthenticated,
  ]);

  return {
    ...currentCart,
    checkoutSummaryLoading,
    isLoading,
  };
};
